import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "../api/client.js";
import { OpsHistoryShell } from "../components/OpsHistoryShell.js";
import { NotificationRow } from "../components/OpsRows.js";

/** Every notification Mosaic has raised, filterable by kind, delivery and text. */
export function NotificationsPage() {
  const [kind, setKind] = useState("");
  const [delivery, setDelivery] = useState<"" | "delivered" | "undelivered">("");
  const [draft, setDraft] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const kinds = useQuery({ queryKey: ["notify", "kinds"], queryFn: api.notifyKinds });

  const history = useInfiniteQuery({
    queryKey: ["notify", "history", { kind, delivery, q }],
    queryFn: ({ pageParam }) =>
      api.notifyHistory({
        ...(kind ? { kind } : {}),
        ...(delivery ? { delivery } : {}),
        ...(q ? { q } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: "" as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items = useMemo(() => history.data?.pages.flatMap((p) => p.items) ?? [], [history.data]);
  const total = history.data?.pages[0]?.total ?? null;
  const filtered = Boolean(kind || delivery || q);

  return (
    <OpsHistoryShell
      title="Notifications"
      deck="Everything Mosaic decided was worth interrupting for. The row is written before anything is sent, so an outage cannot lose the event — only its delivery."
      count={
        total === null
          ? `${items.length} shown`
          : `${items.length} of ${total.toLocaleString()} notification${total === 1 ? "" : "s"}`
      }
      note={filtered ? " — filtered" : null}
      isPending={history.isPending}
      error={history.isError ? (history.error as Error) : null}
      isEmpty={items.length === 0}
      emptyFiltered={filtered}
      emptyAll="Nothing has been raised yet."
      onLoadMore={history.hasNextPage ? () => void history.fetchNextPage() : null}
      loadingMore={history.isFetchingNextPage}
      filters={
        <form
          className="histfilters"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(draft.trim());
          }}
        >
          <label>
            <span className="k">Kind</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">any</option>
              {(kinds.data?.kinds ?? []).map((k) => (
                <option key={k} value={k}>
                  {k.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="k">Delivery</span>
            {/*
              The filter this page exists for: a channel that stopped accepting posts shows
              as a run of "not sent", which a short tail cannot show.
            */}
            <select
              value={delivery}
              onChange={(e) => setDelivery(e.target.value as "" | "delivered" | "undelivered")}
            >
              <option value="">any</option>
              <option value="delivered">delivered</option>
              <option value="undelivered">not sent</option>
            </select>
          </label>
          <label className="histsearch">
            <span className="k">Title or message</span>
            <input
              value={draft}
              placeholder="SanDisk, terms changed, stage advanced…"
              onChange={(e) => setDraft(e.target.value)}
            />
          </label>
          <button type="submit" className="iconbtn">
            Search
          </button>
          {filtered && (
            <button
              type="button"
              className="iconbtn"
              onClick={() => {
                setKind("");
                setDelivery("");
                setDraft("");
                setQ("");
              }}
            >
              Clear
            </button>
          )}
        </form>
      }
    >
      {items.map((n) => (
        <NotificationRow
          key={n.id}
          notification={n}
          open={openId === n.id}
          onToggle={() => setOpenId(openId === n.id ? null : n.id)}
        />
      ))}
    </OpsHistoryShell>
  );
}
